/**
 * AssignedDevices.js
 *
 * Displays all devices assigned to the Technician as primary or secondary technician
 * Provides filtering and search capabilities, including active complaint counts
 * (device → complaint signal; complements dashboard Assigned Complaints)
 *
 * Props:
 * - token: JWT authentication token
 * - user: Current user object
 * - onViewDevice: Callback when viewing a device
 * - onSourceChange: Callback to set navigation source for back button
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { DeviceIDBadge, AssignmentBadge } from './Badges';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import './AssignedDevices.css';

const AssignedDevices = ({ token, user, onViewDevice, onSourceChange }) => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, primary, secondary, has-complaints

  useEffect(() => {
    fetchAssignedDevices();
  }, [token]);

  const fetchAssignedDevices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(API_ENDPOINTS.DEVICES.ASSIGNED_TO_ME, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDevices(response.data.devices || []);
    } catch (err) {
      console.error('Error fetching assigned devices:', err);
      setError('Failed to load assigned devices');
      toast.error('Failed to load assigned devices');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveComplaints = (device) => (device.active_complaint_count || 0) > 0;

  const getDeviceUrgency = (device) => {
    // Returns a priority score for sorting (lower = higher priority/more urgent)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let maxUrgency = 999; // Default (no urgency)

    // Active complaints are operational work — same top tier as overdue maintenance
    if (hasActiveComplaints(device)) {
      maxUrgency = Math.min(maxUrgency, 0);
    }

    // Check PPM urgency
    if (device.ppm_next_due_date) {
      const dueDate = new Date(device.ppm_next_due_date);
      const daysUntil = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) {
        maxUrgency = Math.min(maxUrgency, 0); // Overdue - highest priority
      } else if (daysUntil <= 7) {
        maxUrgency = Math.min(maxUrgency, 1); // Due soon (within 7 days)
      }
    }

    // Check calibration urgency
    if (device.calibration_required && device.calibration_next_due_date) {
      const calibDate = new Date(device.calibration_next_due_date);
      const daysUntil = Math.round((calibDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntil < 0) {
        maxUrgency = Math.min(maxUrgency, 0); // Overdue - highest priority
      } else if (daysUntil <= 7) {
        maxUrgency = Math.min(maxUrgency, 1); // Due soon (within 7 days)
      }
    }

    return maxUrgency;
  };

  const getFilteredDevices = () => {
    let filtered = devices;

    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        (d.device_identifier && d.device_identifier.toLowerCase().includes(searchLower)) ||
        (d.name && d.name.toLowerCase().includes(searchLower)) ||
        (d.serial_number && d.serial_number.toLowerCase().includes(searchLower)) ||
        (d.model && d.model.toLowerCase().includes(searchLower)) ||
        (d.physical_location && d.physical_location.toLowerCase().includes(searchLower)) ||
        (d.department && d.department.toLowerCase().includes(searchLower))
      );
    }

    // Filter by assignment type or active complaints
    if (filterType === 'primary') {
      filtered = filtered.filter(d => d.is_primary_technician);
    } else if (filterType === 'secondary') {
      filtered = filtered.filter(d => !d.is_primary_technician);
    } else if (filterType === 'has-complaints') {
      filtered = filtered.filter(hasActiveComplaints);
    }

    // Sort by urgency (complaints + maintenance), then primary before secondary,
    // then higher complaint count first within the same tier
    filtered.sort((a, b) => {
      const urgencyA = getDeviceUrgency(a);
      const urgencyB = getDeviceUrgency(b);

      if (urgencyA !== urgencyB) {
        return urgencyA - urgencyB;
      }

      if (a.is_primary_technician !== b.is_primary_technician) {
        return a.is_primary_technician ? -1 : 1;
      }

      const complaintsA = a.active_complaint_count || 0;
      const complaintsB = b.active_complaint_count || 0;
      if (complaintsA !== complaintsB) {
        return complaintsB - complaintsA;
      }

      return 0;
    });

    return filtered;
  };

  /** overdue | due-soon | null — used for attention strip */
  const getDueDateUrgency = (dateString) => {
    if (!dateString) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dueDate = new Date(dateString);
    dueDate.setHours(0, 0, 0, 0);
    const daysUntil = Math.round((dueDate - today) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'overdue';
    if (daysUntil <= 7) return 'due-soon';
    return null;
  };

  const devicesWithComplaintsCount = devices.filter(hasActiveComplaints).length;
  const primaryCount = devices.filter((d) => d.is_primary_technician).length;
  const secondaryCount = devices.filter((d) => !d.is_primary_technician).length;
  const allCount = devices.length;
  const filteredDevices = getFilteredDevices();

  if (loading) {
    return (
      <div className="assigned-devices-container">
        <div className="assigned-devices-header">
          <h2>🖥️ Devices Assigned to Me</h2>
        </div>
        <div className="assigned-devices-loading">
          <div className="spinner"></div>
          <p>Loading devices...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="assigned-devices-container">
        <div className="assigned-devices-header">
          <h2>🖥️ Devices Assigned to Me</h2>
        </div>
        <div className="assigned-devices-error">
          <div className="error-icon">⚠️</div>
          <p>{error}</p>
          <button onClick={fetchAssignedDevices} className="btn-retry">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="assigned-devices-container">
      <div className="assigned-devices-description-section">
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <h1>🖥️ Devices Assigned to Me</h1>
          <button onClick={fetchAssignedDevices} className="btn-refresh" title="Refresh devices" style={{ padding: '0.5rem', fontSize: '1.2rem', border: 'none', background: 'none', cursor: 'pointer', lineHeight: 1 }}>
            🔄
          </button>
        </div>
        <p>View and manage all medical devices assigned to you as primary or secondary technician. Track active complaints, maintenance due dates, calibration schedules, and device status.</p>
      </div>

      {/* Search and Filter Section */}
      <div className="assigned-devices-controls">
        <div className="search-box-wrapper">
          <span className="search-icon-lens">🔍</span>
          <input
            type="text"
            placeholder="Search by Device ID, name, serial number, or model..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input-clean"
          />
          {searchTerm && (
            <button
              className="clear-search-btn-inline"
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="filter-buttons" role="tablist" aria-label="Assignment filters">
          <button
            type="button"
            className={`filter-btn ${filterType === 'all' ? 'active' : ''}`}
            onClick={() => setFilterType('all')}
          >
            <span className="filter-btn-label">All assignments</span>
            <span className="filter-btn-count">{allCount}</span>
          </button>
          <button
            type="button"
            className={`filter-btn ${filterType === 'primary' ? 'active' : ''}`}
            onClick={() => setFilterType('primary')}
          >
            <span className="filter-btn-label">Primary technician</span>
            <span className="filter-btn-count">{primaryCount}</span>
          </button>
          <button
            type="button"
            className={`filter-btn ${filterType === 'secondary' ? 'active' : ''}`}
            onClick={() => setFilterType('secondary')}
          >
            <span className="filter-btn-label">Secondary technician</span>
            <span className="filter-btn-count">{secondaryCount}</span>
          </button>
          <button
            type="button"
            className={`filter-btn ${filterType === 'has-complaints' ? 'active' : ''}`}
            onClick={() => setFilterType('has-complaints')}
            title="Show devices with active complaints"
          >
            <span className="filter-btn-label">Active complaints</span>
            <span className="filter-btn-count">{devicesWithComplaintsCount}</span>
          </button>
        </div>
      </div>

      {/* Devices List */}
      {filteredDevices.length === 0 ? (
        <div className="assigned-devices-empty">
          <p className="empty-icon">📭</p>
          <p className="empty-title">No devices found</p>
          <p className="empty-subtitle">
            {searchTerm
              ? 'Try adjusting your search terms'
              : filterType === 'has-complaints'
                ? 'None of your assigned devices have active complaints'
                : 'No devices assigned to you yet'}
          </p>
        </div>
      ) : (
        <div className="assigned-devices-grid">
          {filteredDevices.map((device) => {
            const complaintCount = device.active_complaint_count || 0;
            const assignedToMeCount =
              device.assigned_to_me_active_complaint_count || 0;
            const assignedToOtherCount =
              device.assigned_to_other_active_complaint_count || 0;
            const unassignedCount =
              device.unassigned_active_complaint_count || 0;
            const hasAssignedToMe = assignedToMeCount > 0;
            const ppmUrgency = getDueDateUrgency(device.ppm_next_due_date);
            const calibrationUrgency = device.calibration_required
              ? getDueDateUrgency(device.calibration_next_due_date)
              : null;
            const attentionLines = [];
            if (assignedToMeCount > 0) {
              attentionLines.push({
                key: 'mine',
                tone: 'mine',
                text: `${assignedToMeCount} complaint${assignedToMeCount === 1 ? '' : 's'} → you`,
              });
            }
            if (assignedToOtherCount > 0) {
              attentionLines.push({
                key: 'other',
                tone: 'muted',
                text: `${assignedToOtherCount} complaint${assignedToOtherCount === 1 ? '' : 's'} → other tech${assignedToOtherCount === 1 ? '' : 's'}`,
              });
            }
            if (unassignedCount > 0) {
              attentionLines.push({
                key: 'unassigned',
                tone: 'muted',
                text: `${unassignedCount} complaint${unassignedCount === 1 ? '' : 's'} unassigned`,
              });
            }
            // Active on device but none in the buckets above (edge) — still surface total
            if (
              complaintCount > 0 &&
              assignedToMeCount === 0 &&
              assignedToOtherCount === 0 &&
              unassignedCount === 0
            ) {
              attentionLines.push({
                key: 'active',
                tone: 'muted',
                text: `${complaintCount} complaint${complaintCount === 1 ? '' : 's'} active on device`,
              });
            }
            if (ppmUrgency === 'overdue') {
              attentionLines.push({ key: 'ppm-overdue', tone: 'danger', text: 'PPM overdue' });
            } else if (ppmUrgency === 'due-soon') {
              attentionLines.push({ key: 'ppm-soon', tone: 'warn', text: 'PPM due soon' });
            }
            if (calibrationUrgency === 'overdue') {
              attentionLines.push({ key: 'cal-overdue', tone: 'danger', text: 'Calibration overdue' });
            } else if (calibrationUrgency === 'due-soon') {
              attentionLines.push({ key: 'cal-soon', tone: 'warn', text: 'Calibration due soon' });
            }
            const attentionTone = hasAssignedToMe
              ? 'mine'
              : attentionLines.some((l) => l.tone === 'danger')
                ? 'danger'
                : attentionLines.some((l) => l.tone === 'warn')
                  ? 'warn'
                  : attentionLines.length > 0
                    ? 'muted'
                    : null;

            return (
              <div
                key={device.id}
                className={`device-card device-card--clickable${
                  hasAssignedToMe ? ' device-card--has-my-complaints' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (onSourceChange) {
                    onSourceChange('assigned-devices');
                  }
                  if (onViewDevice) {
                    onViewDevice(device.id);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (onSourceChange) {
                      onSourceChange('assigned-devices');
                    }
                    if (onViewDevice) {
                      onViewDevice(device.id);
                    }
                  }
                }}
              >
                <div className="card-header">
                  <div className="card-header-top">
                    <DeviceIDBadge deviceId={device.device_identifier} size="medium" />
                    <AssignmentBadge
                      type={device.is_primary_technician ? 'primary' : 'secondary'}
                      size="medium"
                    />
                  </div>
                </div>

                <div className="card-content">
                  <div className="device-name">
                    {device.name || 'N/A'}
                  </div>

                  {(device.serial_number || device.model) && (
                    <div className="device-meta">
                      {device.serial_number && (
                        <div className="device-meta-row">
                          <span className="device-meta-label">Serial</span>
                          <span className="device-meta-item" title={device.serial_number}>
                            {device.serial_number}
                          </span>
                        </div>
                      )}
                      {device.model && (
                        <div className="device-meta-row">
                          <span className="device-meta-label">Model</span>
                          <span className="device-meta-item" title={device.model}>
                            {device.model}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {(device.department || device.physical_location) && (
                    <div className="device-location-quiet">
                      {device.department && (
                        <div className="device-location-row">
                          {device.department_icon && (
                            <span className="device-location-icon" aria-hidden="true">
                              {device.department_icon}
                            </span>
                          )}
                          <span className="device-location-text">{device.department}</span>
                        </div>
                      )}
                      {device.physical_location && (
                        <div className="device-location-row device-location-row--secondary">
                          <span className="device-location-text" title={device.physical_location}>
                            {device.physical_location}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {attentionLines.length > 0 && (
                    <div
                      className={`attention-strip attention-strip--${attentionTone || 'muted'}`}
                      aria-label="Needs attention"
                    >
                      <div className="attention-strip-title">Needs attention</div>
                      <ul className="attention-strip-list">
                        {attentionLines.map((line) => (
                          <li
                            key={line.key}
                            className={`attention-strip-item attention-strip-item--${line.tone}`}
                          >
                            {line.text}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className="card-footer">
                  <span className="device-card-open-hint">View details ›</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssignedDevices;
